import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import { mediaList, genreOptions, regionOptions, yearOptions, categoryMeta } from "@/mocks/media";
import { useSeo } from "@/hooks/useSeo";
import CategoryFilters from "@/pages/category/components/CategoryFilters";

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "" } = useParams();
  const category = decodeURIComponent(type);
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre");

  const [genres, setGenres] = useState<string[]>(
    initialGenre ? [initialGenre] : []
  );
  const [region, setRegion] = useState<string>("");
  const [year, setYear] = useState<number | null>(null);
  const [sort, setSort] = useState<string>("default");
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const meta = categoryMeta.find((c) => c.key === category);
  const metaTitle = meta ? meta.title : category;

  useSeo({
    title: `${metaTitle}大全 - 我的家庭教师`,
    description: `${metaTitle}频道汇集精选${metaTitle}资源，支持按类型、地区、年份多维度筛选与排序，快速找到想看的${metaTitle}，尽在「我的家庭教师」影视频道。`,
    keywords: `我的家庭教师${metaTitle},${metaTitle}大全,${metaTitle}推荐,最新${metaTitle},${metaTitle}在线观看`,
    path: `/category/${encodeURIComponent(category)}`,
  });

  const filtered = useMemo(() => {
    let list = mediaList.filter((item) => item.category === category);
    if (genres.length > 0) {
      list = list.filter((item) => item.genre.some((g) => genres.includes(g)));
    }
    if (region) {
      list = list.filter((item) => item.region === region);
    }
    if (year) {
      list = list.filter((item) => item.year === year);
    }
    if (sort === "date") {
      list = [...list].sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
    } else if (sort === "popularity") {
      list = [...list].sort((a, b) => b.popularity - a.popularity);
    } else {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [category, genres, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const toggleGenre = (g: string) => {
    setPage(1);
    setGenres((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
    );
  };

  const resetFilters = () => {
    setGenres([]);
    setRegion("");
    setYear(null);
    setSort("default");
    setPage(1);
  };

  const hasFilter = genres.length > 0 || !!region || !!year;

  const filterProps = {
    genres,
    region,
    year,
    sort,
    onToggleGenre: toggleGenre,
    onRegion: (r: string) => {
      setPage(1);
      setRegion(r === region ? "" : r);
    },
    onYear: (y: number) => {
      setPage(1);
      setYear(y === year ? null : y);
    },
    onSort: (s: string) => {
      setPage(1);
      setSort(s);
    },
    onReset: resetFilters,
    hasFilter,
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-accent-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{metaTitle}</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold tracking-wide text-foreground-950 md:text-3xl">
          {metaTitle}大全
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          {meta ? meta.subtitle : `浏览精选${metaTitle}内容`}
          ，共收录 <span className="font-medium text-accent-400">{mediaList.filter((m) => m.category === category).length}</span> 部作品。
        </p>
      </header>

      <div className="mt-6 lg:grid lg:grid-cols-[260px_1fr] lg:gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-100 p-5">
            <CategoryFilters {...filterProps} />
          </div>
        </aside>

        <div>
          <div className="mb-4 flex items-center justify-between gap-3 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-4 text-sm text-foreground-800"
            >
              <i className="ri-filter-3-line text-base"></i>
              筛选
              {hasFilter && (
                <span className="h-2 w-2 rounded-full bg-accent-500"></span>
              )}
            </button>
            <span className="text-sm text-foreground-500">
              共 {filtered.length} 部
            </span>
          </div>

          {paged.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-movie-line text-3xl"></i>
              </span>
              <h3 className="mt-4 text-lg font-medium text-foreground-900">
                没有找到匹配的影视内容
              </h3>
              <p className="mt-2 text-sm text-foreground-600">
                试试调整筛选条件，或重置后重新浏览
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-refresh-line text-base"></i>
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="flex h-10 items-center whitespace-nowrap rounded-md border border-background-300/60 px-5 text-sm text-foreground-800 transition-colors hover:text-foreground-950"
                >
                  返回首页
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {paged.map((item) => (
                  <MovieCard key={item.id} item={item} showActors />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300/60 px-3 text-sm text-foreground-700 transition-colors hover:text-foreground-950 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line text-lg"></i>
                    上一页
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={`h-9 w-9 rounded-md text-sm transition-colors ${
                        p === currentPage
                          ? "bg-primary-500 font-semibold text-foreground-950"
                          : "border border-background-300/60 text-foreground-700 hover:text-foreground-950"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300/60 px-3 text-sm text-foreground-700 transition-colors hover:text-foreground-950 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    下一页
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/70"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-lg border-t border-background-300/60 bg-background-100 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                筛选条件
              </h3>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/60 text-foreground-700"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <CategoryFilters {...filterProps} />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="mt-5 h-11 w-full rounded-md bg-accent-500 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}